/**
 * TipTapWYSIWYG — Visual editor entry point.
 *
 * Thin wrapper around the merged block editor (./block/RichTextEditor):
 * real tables, side-by-side columns, cards, steps, accordions, callouts,
 * YouTube/Loom/iframe embeds and a slash-command menu, all stored as
 * Markdown/MDX. Same export + props as before: { content, onChange }.
 *
 * Constructs the editor does not model (Tabs, CodeGroup, Figure, Video, unknown
 * components, raw HTML) are preserved verbatim as raw MDX nodes.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { RichTextEditor } from './block/RichTextEditor';
import { EditorThemeProvider } from './block/EditorThemeContext';
import { EDITOR_THEMES } from './block/editorTheme';

const API = process.env.REACT_APP_BACKEND_URL;

// The editor has its own light/dark choice, separate from the public-site theme.
// Until the author picks one it starts from the current site theme; after that the
// saved choice (localStorage `editor_theme`) wins and the site switch no longer moves it.
const EDITOR_THEME_KEY = 'editor_theme';
const readSiteTheme = () =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light';
const readSavedEditorTheme = () => {
  try {
    const v = window.localStorage.getItem(EDITOR_THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch (e) {
    return null;
  }
};
const useEditorTheme = () => {
  const [saved, setSaved] = useState(readSavedEditorTheme);
  const [site, setSite] = useState(readSiteTheme);
  useEffect(() => {
    if (saved) return undefined;
    const obs = new MutationObserver(() => setSite(readSiteTheme()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [saved]);
  const theme = saved || site;
  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setSaved(next);
    try { window.localStorage.setItem(EDITOR_THEME_KEY, next); } catch (e) { /* private mode */ }
  }, [theme]);
  return [theme, toggle];
};

export const TipTapWYSIWYG = ({ content, onChange }) => {
  const { projectId } = useParams();
  const [themeId, toggleEditorTheme] = useEditorTheme();

  const onUploadImage = useCallback(
    async (file) => {
      if (!projectId) return null;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', '/images');
      const res = await axios.post(`${API}/api/projects/${projectId}/assets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data?.url || null;
    },
    [projectId]
  );

  return (
    <EditorThemeProvider value={themeId}>
      <div
        className={`block-editor-root ${themeId === 'dark' ? 'dark bg-zinc-950' : 'bg-white'} h-full overflow-y-auto px-6 pb-16`}
        data-testid="visual-editor"
        data-theme-id={themeId}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={toggleEditorTheme}
              className={`text-xs px-2 py-1 rounded border ${themeId === 'dark' ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-100'}`}
              data-testid="editor-theme-toggle"
              title="Switch the editing area between light and dark (does not change the public site)"
            >
              {themeId === 'dark' ? 'Editor: dark' : 'Editor: light'}
            </button>
          </div>
          <RichTextEditor
            content={content}
            onChange={onChange}
            theme={EDITOR_THEMES[themeId]}
            onUploadImage={onUploadImage}
            projectId={projectId}
          />
        </div>
      </div>
    </EditorThemeProvider>
  );
};

export default TipTapWYSIWYG;
